import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, API, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  DoorOpenIcon, ScanLineIcon, UsersIcon, LogInIcon, LogOutIcon, UserPlusIcon,
  UploadIcon, DownloadIcon, FileSpreadsheetIcon, Trash2Icon, EditIcon,
  HistoryIcon, FileIcon, CheckCircle2Icon, XCircleIcon, PhoneIcon, TruckIcon, MonitorIcon,
  ClockIcon, FootprintsIcon,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { EtapaBadge, EtapaActions, FINALIDADES, FINALIDADES_FILA, FIN_COR } from "@/components/EtapaOperacao";
import { HistoricoVeiculos } from "@/components/HistoricoVeiculos";
import * as XLSX from "xlsx";

const MOTIVOS_ENTRADA = ["CARGA/DESCARGA", "ENTREGA/COLETA", "VISITA", "TRABALHO/EXPEDIENTE", "SERVIÇO/MANUTENÇÃO", "REUNIÃO", "INSPEÇÃO", "OUTROS"];
const MOTIVOS_SAIDA = ["FIM DE EXPEDIENTE", "ENTREGA CONCLUÍDA", "SERVIÇO CONCLUÍDO", "CARGA FINALIZADA", "ALMOÇO", "OUTROS"];

// Tipo de pessoa na portaria: define quais campos aparecem na Entrada Manual
const TIPOS_PESSOA = [
  ["VISITANTE", "VISITANTE", "#00E5FF"],
  ["MOTORISTA", "MOTORISTA", "#FFD60A"],
  ["COLABORADOR", "COLABORADOR", "#34C759"],
];
const MOTIVO_PADRAO = { VISITANTE: "VISITA", MOTORISTA: "CARGA/DESCARGA", COLABORADOR: "TRABALHO/EXPEDIENTE" };
const tipoPessoaAuto = (p) => {
  if (["VISITANTE", "MOTORISTA", "COLABORADOR"].includes(p?.tipo_pessoa)) return p.tipo_pessoa;
  if (p?.placa_cavalo || p?.placa_carreta) return "MOTORISTA";
  if (p?.matricula) return "COLABORADOR";
  return "VISITANTE";
};

const soDigitos = (v) => String(v || "").replace(/\D/g, "");

// Briefing de segurança vale 6 MESES. Nas próximas entradas vem marcado automaticamente,
// a não ser que esteja VENCIDO ou VENCENDO (≤ 15 dias) — aí o operador precisa refazer.
const BRIEFING_VALIDADE_DIAS = 182;
const BRIEFING_AVISO_DIAS = 15;
const briefingStatus = (p) => {
  if (!p?.briefing_em) return { estado: "SEM" };
  const feito = new Date(p.briefing_em);
  const validoAte = new Date(feito.getTime() + BRIEFING_VALIDADE_DIAS * 86400000);
  const dias = Math.floor((validoAte - Date.now()) / 86400000);
  const estado = dias < 0 ? "VENCIDO" : dias <= BRIEFING_AVISO_DIAS ? "VENCENDO" : "VALIDO";
  return { estado, validoAte, dias };
};
const briefingAuto = (p) => briefingStatus(p).estado === "VALIDO";
const fmtData = (d) => (d ? new Date(d).toLocaleDateString("pt-BR") : "—");

const emptyEntrada = { pessoa_id: "", tipo_pessoa: "VISITANTE", motivo: MOTIVO_PADRAO.VISITANTE, briefing: false, empresa: "", telefone: "", placa_cavalo: "", placa_carreta: "", frota: "", chave_temporaria: "" };

// Sinal sonoro via Web Audio
function beep(freq = 880, dur = 0.14, times = 1) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    let t = ctx.currentTime;
    for (let i = 0; i < times; i++) {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.frequency.value = freq; o.type = "square";
      g.gain.setValueAtTime(0.08, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + dur);
      o.start(t); o.stop(t + dur);
      t += dur + 0.09;
    }
  } catch { /* audio bloqueado */ }
}

const fmtDT = (s) => (s ? new Date(s).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");
const fmtPerm = (min) => {
  if (min == null) return "—";
  if (min < 60) return `${min} min`;
  return `${Math.floor(min / 60)}h ${min % 60}min`;
};

const inputCls = "mt-1 rounded-none bg-[#0A0A0C] border-border h-10";

export default function Portaria() {
  const { user } = useAuth();
  const canDelete = user?.role === "admin" || user?.role === "manager";
  const [stats, setStats] = useState({ dentro: 0, entradas_hoje: 0, saidas_hoje: 0 });
  const [dentro, setDentro] = useState([]);
  const [pessoas, setPessoas] = useState([]);
  const [registros, setRegistros] = useState([]);
  const [scanCode, setScanCode] = useState("");
  const [scanFeedback, setScanFeedback] = useState(null); // {acao, nome} | {notfound}
  const scanRef = useRef(null);
  const fileRef = useRef(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [tab, setTab] = useState("controle");
  const [searchPessoa, setSearchPessoa] = useState("");
  const [searchReg, setSearchReg] = useState("");
  // Histórico completo com filtros
  const [regView, setRegView] = useState("hoje"); // hoje | historico
  const [histFiltroTipo, setHistFiltroTipo] = useState("todos"); // todos | data | mes | ano
  const [histData, setHistData] = useState("");
  const [histMes, setHistMes] = useState("");
  const [histAno, setHistAno] = useState(String(new Date().getFullYear()));
  const [histRegistros, setHistRegistros] = useState([]);
  const [histLoading, setHistLoading] = useState(false);

  // Modais
  const emptyPessoa = { nome: "", cpf: "", matricula: "", chave: "", tipo_cracha: "FIXO", tipo_pessoa: "VISITANTE", empresa: "", telefone: "", placa_cavalo: "", placa_carreta: "", frota: "" };
  const [cadOpen, setCadOpen] = useState(false);
  const [cadForm, setCadForm] = useState(emptyPessoa);
  const [cadCrachaProv, setCadCrachaProv] = useState("");
  const [editingPessoa, setEditingPessoa] = useState(null);
  const [entradaOpen, setEntradaOpen] = useState(false);
  const [scanPendente, setScanPendente] = useState("");
  const [aguardando, setAguardando] = useState([]);
  const [aguardandoSel, setAguardandoSel] = useState(null); // entrada a partir da fila de espera
  const [entradaForm, setEntradaForm] = useState({ ...emptyEntrada });
  const [tipoManual, setTipoManual] = useState(false); // operador escolheu o tipo na mão → não sobrescrever
  const [entradaBusca, setEntradaBusca] = useState("");
  const [saidaOpen, setSaidaOpen] = useState(false);
  const [saidaForm, setSaidaForm] = useState({ pessoa_id: "", motivo: "FIM DE EXPEDIENTE", qtd_tq: 0, qtd_rf: 0, qtd_cesta: 0, cracha_devolvido: false });
  const [saidaEtapas, setSaidaEtapas] = useState({});      // saída COM carreta: o que ela fez nesta visita
  const [saidaDestino, setSaidaDestino] = useState("");    // carregou LOAD → destino (padrão PORTO)
  const [wipeOpen, setWipeOpen] = useState(false);
  const [wipeRegistros, setWipeRegistros] = useState(false);
  const [wipeConfirm, setWipeConfirm] = useState("");
  const [wiping, setWiping] = useState(false);

  const load = async () => {
    const [s, d, p, r, ag] = await Promise.all([
      api.get("/portaria/stats").catch(() => ({ data: { dentro: 0, entradas_hoje: 0, saidas_hoje: 0 } })),
      api.get("/portaria/dentro").catch(() => ({ data: [] })),
      api.get("/portaria/pessoas").catch(() => ({ data: [] })),
      api.get("/portaria/registros").catch(() => ({ data: [] })),
      api.get("/portaria/aguardando").catch(() => ({ data: [] })),
    ]);
    setStats(s.data);
    setDentro(d.data);
    setPessoas(p.data);
    setRegistros(r.data);
    setAguardando(ag.data);
  };
  useEffect(() => { load(); }, []);

  // Carrega histórico completo conforme filtros
  const loadHistorico = async () => {
    setHistLoading(true);
    const params = {};
    if (histFiltroTipo === "data" && histData) params.data = histData;
    if (histFiltroTipo === "mes" && histMes) params.mes = histMes;
    if (histFiltroTipo === "ano" && histAno) params.ano = histAno;
    try {
      const { data } = await api.get("/portaria/registros", { params });
      setHistRegistros(data);
    } catch { setHistRegistros([]); }
    finally { setHistLoading(false); }
  };
  useEffect(() => {
    if (regView === "historico") loadHistorico();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regView, histFiltroTipo, histData, histMes, histAno]);

  // Foco automático no leitor
  useEffect(() => {
    const t = setInterval(() => {
      const el = scanRef.current;
      if (el && document.activeElement?.tagName !== "INPUT" && document.activeElement?.tagName !== "TEXTAREA" && !document.querySelector("[role=dialog]")) {
        el.focus();
      }
    }, 1500);
    return () => clearInterval(t);
  }, []);

  const bipar = async (e) => {
    e.preventDefault();
    const codigo = scanCode.trim();
    if (!codigo) return;
    setScanCode("");
    try {
      const { data } = await api.post("/portaria/bipar", { codigo });
      if (!data.found) {
        beep(220, 0.3, 1);
        setScanFeedback({ notfound: true, codigo });
        // Código não vinculado: pode ser crachá ROTATIVO p/ pessoa JÁ CADASTRADA → abre Entrada com busca
        setScanPendente(codigo);
        setEntradaForm({ ...emptyEntrada, chave_temporaria: codigo });
        setEntradaBusca("");
        setEntradaOpen(true);
        toast.warning(`Crachá ${codigo} não vinculado — busque a pessoa cadastrada ou cadastre nova`);
        return;
      }
      if (data.acao === "ENTRADA") {
        beep(880, 0.14, 1);
        setScanFeedback({ acao: "ENTRADA", nome: data.pessoa.nome });
        toast.success(`ENTRADA registrada: ${data.pessoa.nome}`);
      } else if (data.acao === "AGUARDANDO") {
        // Motorista na fila que já recebeu o crachá: bipar = ENTRADA A PÉ (banheiro, café, doc.) e o veículo
        // continua na FILA DE ESPERA. Para ENTRAR COM O VEÍCULO use o botão ENTRAR na fila (ou a Entrada Manual).
        beep(660, 0.14, 1);
        await api.post("/portaria/entrada", {
          pessoa_id: data.pessoa.id,
          tipo_pessoa: "MOTORISTA",
          motivo: "PEDESTRE — VEÍCULO NA FILA",
          briefing: false,
          chave_temporaria: data.aguardando?.chave_temporaria || data.pessoa.chave || codigo,
          placa_cavalo: "",
          placa_carreta: "",
          finalizar_aguardando: false,
        });
        setScanFeedback({ acao: "ENTRADA", nome: `${data.pessoa.nome} (A PÉ — veículo na fila)` });
        toast.success(`ENTRADA A PÉ: ${data.pessoa.nome} — o veículo continua na FILA DE ESPERA (${finsLabel(data.aguardando)}). Para entrar com o veículo use ENTRAR na fila.`);
      } else {
        beep(520, 0.14, 2);
        setScanFeedback({ acao: "SAIDA", nome: data.pessoa.nome });
        toast.info(`SAÍDA registrada: ${data.pessoa.nome}` + (data.cracha_mantido ? ` · crachá ${data.cracha_mantido} permanece com a pessoa (marque a devolução quando entregar)` : ""));
      }
      load();
    } catch (err) {
      beep(220, 0.3, 1);
      toast.error(formatApiError(err.response?.data?.detail));
    }
    setTimeout(() => setScanFeedback(null), 5000);
  };

  const salvarCadastro = async () => {
    if (!cadForm.nome.trim()) return toast.error("Nome é obrigatório");
    if (cpfDuplicado) return toast.error(`${cpfDuplicado.nome} já está cadastrado(a) com o CPF ${cpfDuplicado.cpf} — use o cadastro existente`);
    try {
      if (editingPessoa) {
        const payload = {};
        Object.keys(emptyPessoa).forEach((k) => {
          if ((cadForm[k] || "") !== (editingPessoa[k] || "")) payload[k] = cadForm[k];
        });
        if (!Object.keys(payload).length) { toast.info("Nada para salvar"); return; }
        await api.patch(`/portaria/pessoas/${editingPessoa.id}`, payload);
        toast.success("Cadastro atualizado");
      } else {
        const { data: nova } = await api.post("/portaria/pessoas", cadForm);
        toast.success(`${cadForm.nome} cadastrado(a)`);
        // ROTATIVO com nº de crachá provisório informado → já abre a ENTRADA vinculando o crachá
        if (nova?.id && cadForm.tipo_cracha === "ROTATIVO" && cadCrachaProv.trim()) {
          const prov = cadCrachaProv.trim();
          await load();
          setCadOpen(false);
          setCadForm(emptyPessoa);
          setEditingPessoa(null);
          const tipoNova = tipoPessoaAuto(nova);
          setEntradaForm({ ...emptyEntrada, pessoa_id: nova.id, tipo_pessoa: tipoNova, motivo: MOTIVO_PADRAO[tipoNova], empresa: nova.empresa || "", telefone: nova.telefone || "", placa_cavalo: nova.placa_cavalo || "", placa_carreta: nova.placa_carreta || "", frota: nova.frota || "", chave_temporaria: prov });
          setCadCrachaProv("");
          setEntradaOpen(true);
          toast.info(`Confirme a ENTRADA de ${nova.nome} com o crachá ${prov}`);
          return;
        }
      }
      setCadOpen(false);
      setCadForm(emptyPessoa);
      setCadCrachaProv("");
      setEditingPessoa(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const excluirPessoa = async (p) => {
    if (!window.confirm(`Excluir cadastro de ${p.nome}?`)) return;
    try {
      await api.delete(`/portaria/pessoas/${p.id}`);
      toast.success("Cadastro excluído");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const excluirTodos = async () => {
    if (wipeConfirm.trim().toUpperCase() !== "APAGAR") return toast.error('Digite APAGAR para confirmar');
    setWiping(true);
    try {
      const { data } = await api.delete(`/portaria/pessoas?incluir_registros=${wipeRegistros}`);
      toast.success(`${data.pessoas_excluidas} cadastro(s) excluído(s)` + (wipeRegistros ? ` · ${data.registros_excluidos} registro(s) de entrada/saída apagados` : ""));
      setWipeOpen(false);
      setWipeConfirm("");
      setWipeRegistros(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setWiping(false); }
  };

  const finsLabel = (a) => (a?.finalidades || [a?.finalidade]).filter(Boolean).map((f) => FINALIDADES[f] || f).join(" + ");
  // Fila do lado de fora (AGUARDANDO); EM_OPERACAO já entrou e aparece na lista "na base" com a etapa
  const filaFora = useMemo(() => aguardando.filter((a) => a.status === "AGUARDANDO"), [aguardando]);
  const [chegadaFins, setChegadaFins] = useState({ DESCARREGAR_CHEIOS: true, CARREGAR_VAZIOS: false, DESCARREGAR_BACKLOAD: false, CARREGAR_LOAD: false, CARRETA_VAZIA_STANDBY: false });
  const [standbyFins, setStandbyFins] = useState({ CARREGAR_LOAD: false, CARREGAR_VAZIOS: false });
  const [entradaDestino, setEntradaDestino] = useState("PORTO");  // frota que vai CARREGAR LOAD: destino (Telão mostra)

  const DestinoLoad = ({ testid }) => (
    <div data-testid={testid}>
      <Label className="text-[10px] uppercase tracking-wider text-[#34C759]">Destino do Load (frota {entradaForm.frota})</Label>
      <div className="flex gap-2 mt-1">
        <Input data-testid={`${testid}-input`} value={entradaDestino} onChange={(e) => setEntradaDestino(e.target.value.toUpperCase())} className={`${inputCls} font-mono flex-1`} placeholder="PORTO" />
        {["PORTO", "CLIENTE", "OUTRA BASE"].map((d) => (
          <button key={d} type="button" onClick={() => setEntradaDestino(d)} data-testid={`${testid}-${d.replace(" ", "_")}`}
                  className={`border px-2 h-10 text-[10px] font-bold uppercase ${entradaDestino === d ? "bg-[#34C759] text-black border-[#34C759]" : "border-[#34C759]/50 text-[#34C759] hover:bg-[#34C759]/10"}`}>{d}</button>
        ))}
      </div>
    </div>
  );

  // Veículo na fila só em STANDBY (carreta vazia): ao entrar precisa dizer o que vai fazer (carregar load / carregar vazios)
  const emStandby = (entry) => {
    const fins = (entry?.finalidades || [entry?.finalidade]).filter(Boolean);
    const et = entry?.etapas || {};
    return fins.includes("CARRETA_VAZIA_STANDBY") && fins.every((f) => f === "CARRETA_VAZIA_STANDBY" || ["CONCLUIDO", "CANCELADO"].includes(et[f]));
  };

  const abrirEntradaDaFila = (entry, pessoa) => {
    setAguardandoSel(entry);
    setStandbyFins({ CARREGAR_LOAD: false, CARREGAR_VAZIOS: false });
    setEntradaDestino(entry.destino || "PORTO");
    setEntradaForm({
      pessoa_id: entry.pessoa_id,
      tipo_pessoa: "MOTORISTA",
      motivo: "CARGA/DESCARGA",
      briefing: briefingAuto(pessoa),
      empresa: pessoa?.empresa || entry.empresa || "",
      telefone: pessoa?.telefone || "",
      placa_cavalo: entry.placa_cavalo || pessoa?.placa_cavalo || "",
      placa_carreta: entry.placa_carreta || pessoa?.placa_carreta || "",
      frota: entry.frota || pessoa?.frota || "",
      chave_temporaria: entry.chave_temporaria || pessoa?.chave || "",
    });
    setEntradaBusca("");
    setEntradaOpen(true);
    toast.info(`${entry.nome} está na FILA DE ESPERA (${finsLabel(entry)}) — entrada com veículo ou a pé?`);
  };

  // Bipe do crachá DENTRO da Entrada Manual: identifica a pessoa e, se ela estiver na fila,
  // mostra a escolha ENTRAR A PÉ / ENTRAR COM VEÍCULO (no leitor da tela principal o bipe já entra a pé).
  const resolverCrachaNaEntrada = (codigo) => {
    const cod = (codigo || "").trim();
    if (!cod) return;
    const pessoa = pessoas.find((p) => (p.chave || "").trim().toUpperCase() === cod.toUpperCase());
    if (!pessoa) return toast.warning(`Crachá ${cod} não vinculado — busque a pessoa cadastrada acima`);
    const naFila = aguardando.find((a) => a.pessoa_id === pessoa.id && a.status === "AGUARDANDO");
    if (naFila) return abrirEntradaDaFila(naFila, pessoa);
    const tp = pessoa.tipo_pessoa || tipoPessoaAuto(pessoa);
    setAguardandoSel(null);
    setEntradaForm({ ...entradaForm, pessoa_id: pessoa.id, tipo_pessoa: tp, motivo: MOTIVO_PADRAO[tp], briefing: briefingAuto(pessoa), empresa: pessoa.empresa || "", telefone: pessoa.telefone || "", placa_cavalo: pessoa.placa_cavalo || "", placa_carreta: pessoa.placa_carreta || "", frota: pessoa.frota || "", chave_temporaria: cod });
    setEntradaBusca("");
    toast.success(`${pessoa.nome} identificado(a) pelo crachá ${cod}`);
  };

  const marcarChegada = async () => {
    if (!entradaForm.pessoa_id) return toast.error("Selecione a pessoa");
    const finalidades = Object.keys(chegadaFins).filter((k) => chegadaFins[k]);
    if (!finalidades.length) return toast.error("Marque a finalidade: descarregar cheios, carregar vazios e/ou descarregar backload");
    if (!entradaForm.placa_cavalo.trim() && !entradaForm.placa_carreta.trim()) {
      const sel = pessoas.find((p) => p.id === entradaForm.pessoa_id);
      if (!sel?.placa_cavalo && !sel?.placa_carreta) return toast.error("Informe a placa do veículo para colocar na fila de espera");
    }
    try {
      await api.post("/portaria/chegada", {
        pessoa_id: entradaForm.pessoa_id,
        finalidades,
        placa_cavalo: entradaForm.placa_cavalo || null,
        placa_carreta: entradaForm.placa_carreta || null,
        frota: entradaForm.frota || null,
        chave_temporaria: entradaForm.chave_temporaria || null,
        destino: entradaForm.frota && finalidades.includes("CARREGAR_LOAD") ? (entradaDestino.trim() || null) : null,
      });
      beep(660, 0.14, 1);
      toast.success(`Chegada registrada — aguardando entrada para ${finalidades.map((f) => FINALIDADES[f]).join(" + ")}`);
      setEntradaOpen(false);
      setScanPendente("");
      setAguardandoSel(null);
      setTipoManual(false);
      setEntradaForm({ ...emptyEntrada });
      setEntradaBusca("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const cancelarEspera = async (entry) => {
    if (!window.confirm(`Remover ${entry.nome} da fila de espera?`)) return;
    try {
      await api.delete(`/portaria/aguardando/${entry.id}`);
      toast.success("Removido da fila de espera");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const registrarEntrada = async (opts = {}) => {
    if (!entradaForm.pessoa_id) return toast.error("Selecione a pessoa");
    const motivo = opts.motivo || entradaForm.motivo;
    if (!motivo) return toast.error("Informe o motivo da entrada");
    const sel = pessoas.find((p) => p.id === entradaForm.pessoa_id);
    if (sel?.tipo_cracha === "ROTATIVO" && !entradaForm.chave_temporaria.trim()) {
      return toast.error("Bipe ou digite o Número do Crachá Provisório para dar entrada");
    }
    const standbyEscolhidas = Object.keys(standbyFins).filter((k) => standbyFins[k]);
    if (opts.finalizar && emStandby(aguardandoSel) && !standbyEscolhidas.length) {
      return toast.error("Informe o que a frota vai fazer agora: Carregar Load e/ou Carregar Vazios");
    }
    // Entrada DIRETA com veículo (sem fila): usa as finalidades marcadas na seção "Chegou com veículo"
    const diretaFins = opts.direto ? Object.keys(chegadaFins).filter((k) => chegadaFins[k] && k !== "CARRETA_VAZIA_STANDBY") : [];
    if (opts.direto) {
      if (!diretaFins.length) return toast.error("Marque pelo menos uma finalidade (descarregar cheios, carregar vazios, descarregar backload ou carregar load) para entrar com o veículo");
      if (!entradaForm.placa_cavalo.trim() && !entradaForm.placa_carreta.trim() && !sel?.placa_cavalo && !sel?.placa_carreta) return toast.error("Informe a placa do veículo para entrar com o veículo");
    }
    const finsEscolhidas = opts.direto ? diretaFins : standbyEscolhidas;
    try {
      const isMotorista = entradaForm.tipo_pessoa === "MOTORISTA";
      const payload = {
        pessoa_id: entradaForm.pessoa_id,
        tipo_pessoa: entradaForm.tipo_pessoa,
        motivo: opts.finalizar && finsEscolhidas.length ? finsEscolhidas.map((f) => FINALIDADES[f]).join(" + ") : motivo,
        briefing: entradaForm.tipo_pessoa === "COLABORADOR" ? null : entradaForm.briefing,
        chave_temporaria: entradaForm.chave_temporaria,
        finalizar_aguardando: !!opts.finalizar,
      };
      if (opts.finalizar && finsEscolhidas.length) payload.finalidades = finsEscolhidas;
      if (opts.finalizar && entradaForm.frota && finsEscolhidas.includes("CARREGAR_LOAD")) {
        if (!entradaDestino.trim()) return toast.error("Informe o DESTINO do load (ex.: PORTO)");
        payload.destino = entradaDestino.trim().toUpperCase();
      }
      if (isMotorista) {
        payload.empresa = entradaForm.empresa;
        payload.telefone = entradaForm.telefone;
        payload.placa_cavalo = opts.semVeiculo ? "" : entradaForm.placa_cavalo;
        payload.placa_carreta = opts.semVeiculo ? "" : entradaForm.placa_carreta;
        payload.frota = opts.semVeiculo ? "" : entradaForm.frota;
      }
      await api.post("/portaria/entrada", payload);
      beep(880, 0.14, 1);
      toast.success("Entrada registrada" + (sel?.tipo_cracha === "ROTATIVO" ? ` · crachá ${entradaForm.chave_temporaria} vinculado` : "") + (opts.direto ? " · entrou com o veículo — operação iniciada" : opts.finalizar ? " · removido da fila de espera" : ""));
      setEntradaOpen(false);
      setScanPendente("");
      setAguardandoSel(null);
      setTipoManual(false);
      setEntradaForm({ ...emptyEntrada });
      setEntradaBusca("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const registrarSaida = async () => {
    if (!saidaForm.pessoa_id) return toast.error("Selecione quem está saindo");
    const regSel = dentro.find((r) => r.pessoa_id === saidaForm.pessoa_id);
    try {
      const comCarreta = !!(regSel?.com_veiculo && regSel?.operacao);
      const realizadas = Object.keys(saidaEtapas).filter((k) => saidaEtapas[k]);
      if (comCarreta && regSel?.frota && realizadas.includes("CARREGAR_LOAD") && !saidaDestino.trim()) return toast.error("Informe o DESTINO do load (ex.: PORTO)");
      const { data: saida } = await api.post("/portaria/saida", {
        ...saidaForm,
        qtd_tq: parseInt(saidaForm.qtd_tq || 0, 10),
        qtd_rf: parseInt(saidaForm.qtd_rf || 0, 10),
        qtd_cesta: parseInt(saidaForm.qtd_cesta || 0, 10),
        ...(comCarreta ? { etapas_realizadas: realizadas, destino: regSel?.frota && realizadas.includes("CARREGAR_LOAD") ? saidaDestino.trim().toUpperCase() : null } : {}),
      });
      beep(520, 0.14, 2);
      const infoCracha = regSel?.cracha_temporario
        ? (saidaForm.cracha_devolvido ? ` · crachá ${regSel.cracha_temporario} devolvido e liberado` : ` · crachá ${regSel.cracha_temporario} permanece com a pessoa`)
        : "";
      toast.success("Saída registrada" + infoCracha);
      const tr = saida?.registro?.transito_auto;
      if (tr) toast.info(`FROTA ${tr.frota} saiu CARREGADA → ${tr.destino}. Aparece no Telão no card "Saiu p/ PORTO" até voltar.`, { duration: 8000 });
      const sb = saida?.registro?.standby_auto || saida?.standby_auto;
      if (sb) {
        toast.info(`FROTA ${sb.frota} descarregou o backload → ${sb.placa_cavalo || sb.placa_carreta} foi para a fila CARRETA VAZIA / STANDBY`, { duration: 8000 });
      }
      setSaidaOpen(false);
      setSaidaForm({ pessoa_id: "", motivo: "FIM DE EXPEDIENTE", qtd_tq: 0, qtd_rf: 0, qtd_cesta: 0, cracha_devolvido: false });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const devolverCracha = async (p) => {
    const numero = p.cracha_atual || p.chave;
    if (!window.confirm(`Confirmar devolução do crachá provisório ${numero} por ${p.nome}? O número ficará livre para outra pessoa.`)) return;
    try {
      await api.post(`/portaria/pessoas/${p.id}/devolver-cracha`);
      toast.success(`Crachá ${numero} devolvido e liberado para reuso`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const abrirSaidaPara = (reg) => {
    setSaidaForm({ pessoa_id: reg.pessoa_id, motivo: "FIM DE EXPEDIENTE", qtd_tq: 0, qtd_rf: 0, qtd_cesta: 0, cracha_devolvido: false });
    setSaidaOpen(true);
  };

  const selectFile = (file) => {
    if (!file) return;
    if (!/\.(xlsx|xls|csv)$/i.test(file.name)) return toast.error("Arquivo inválido — use .xlsx, .xls ou .csv");
    if (file.size > 10 * 1024 * 1024) return toast.error("Arquivo grande demais (limite 10MB)");
    setPendingFile(file);
  };

  const importar = async () => {
    if (!pendingFile) return;
    setUploading(true);
    try {
      // Lê a planilha NO NAVEGADOR e envia apenas JSON (evita bloqueio de upload por firewall corporativo)
      const buf = await pendingFile.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: "", raw: false });
      if (!rows.length) { toast.error("Planilha vazia ou sem linhas válidas"); setUploading(false); return; }
      const { data } = await api.post("/portaria/pessoas/import-json", { rows, filename: pendingFile.name });
      toast.success(`${data.inseridos} cadastros realizados, ${data.duplicados} duplicados ignorados`);
      if (data.erros?.length) toast.warning(`${data.erros.length} linhas com erro`);
      setPendingFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) {
      toast.error(e.response ? formatApiError(e.response?.data?.detail) : `Erro lendo a planilha: ${e.message}`);
    }
    finally { setUploading(false); }
  };

  const baixarTemplate = async () => {
    try {
      const res = await api.get("/portaria/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "modelo_portaria_pessoas.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Erro baixando modelo"); }
  };

  const pessoasFiltradas = useMemo(() => pessoas.filter((p) => {
    if (!searchPessoa) return true;
    const s = searchPessoa.toLowerCase();
    const d = soDigitos(searchPessoa);
    return ["nome", "cpf", "matricula", "chave", "empresa", "telefone"].some((k) => String(p[k] || "").toLowerCase().includes(s))
      || (!!d && soDigitos(p.cpf).includes(d));
  }), [pessoas, searchPessoa]);

  const hojeIso = new Date().toISOString().slice(0, 10);
  const registrosHoje = useMemo(() => registros.filter((r) => (r.hora_entrada || "").slice(0, 10) === hojeIso).filter((r) => {
    if (!searchReg) return true;
    const s = searchReg.toLowerCase();
    return ["nome", "cpf", "empresa", "motivo_entrada", "motivo_saida", "placa_cavalo"].some((k) => String(r[k] || "").toLowerCase().includes(s));
  }), [registros, searchReg, hojeIso]);

  const histFiltrados = useMemo(() => histRegistros.filter((r) => {
    if (!searchReg) return true;
    const s = searchReg.toLowerCase();
    return ["nome", "cpf", "empresa", "motivo_entrada", "motivo_saida", "placa_cavalo"].some((k) => String(r[k] || "").toLowerCase().includes(s));
  }), [histRegistros, searchReg]);

  const pessoasForaBase = useMemo(() => pessoas.filter((p) => p.status !== "DENTRO"), [pessoas]);
  // O CPF identifica a pessoa (nomes podem repetir, CPF não) → avisa antes de salvar um cadastro duplicado
  const cpfDuplicado = useMemo(() => {
    const d = soDigitos(cadForm.cpf);
    if (d.length < 11) return null;
    return pessoas.find((p) => soDigitos(p.cpf) === d && p.id !== editingPessoa?.id) || null;
  }, [cadForm.cpf, pessoas, editingPessoa]);
  const entradaCandidatas = useMemo(() => pessoasForaBase.filter((p) => {
    if (!entradaBusca) return true;
    const s = entradaBusca.toLowerCase();
    const d = soDigitos(entradaBusca);
    return ["nome", "cpf", "matricula", "empresa"].some((k) => String(p[k] || "").toLowerCase().includes(s))
      || (!!d && soDigitos(p.cpf).includes(d));
  }).slice(0, 8), [pessoasForaBase, entradaBusca]);

  const pessoaSelEntrada = pessoas.find((p) => p.id === entradaForm.pessoa_id);
  const regSaidaSel = dentro.find((r) => r.pessoa_id === saidaForm.pessoa_id);
  const saidaComCarreta = !!(regSaidaSel?.com_veiculo && regSaidaSel?.operacao);
  const saidaFins = saidaComCarreta ? (regSaidaSel.operacao.finalidades || [regSaidaSel.operacao.finalidade]).filter(Boolean) : [];
  useEffect(() => {
    if (!saidaComCarreta) { setSaidaEtapas({}); setSaidaDestino(""); return; }
    const et = regSaidaSel.operacao.etapas || {};
    const init = {};
    saidaFins.forEach((f) => { init[f] = ["CONCLUIDO", "EM_ANDAMENTO"].includes(et[f]); });
    setSaidaEtapas(init);
    setSaidaDestino(saidaFins.includes("CARREGAR_LOAD") && regSaidaSel.frota ? (regSaidaSel.operacao.destino || regSaidaSel.destino || "PORTO") : "");
    setSaidaForm((f) => ({ ...f, motivo: "CARGA FINALIZADA" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regSaidaSel?.id, saidaOpen]);

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="portaria-page">
      {/* HEADER FIXO */}
      <div className="flex flex-col lg:flex-row gap-4 lg:items-stretch">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#00E5FF] mb-1 flex items-center gap-2">
            <DoorOpenIcon className="w-4 h-4" /> Portaria & Controle de Acesso
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-none">Controle da <span className="text-[#FFD60A]">Base</span></h1>
          <a href="/portaria/telao" target="_blank" rel="noreferrer" data-testid="btn-telao"
             className="inline-flex items-center gap-2 mt-3 border border-[#00E5FF] text-[#00E5FF] hover:bg-[#00E5FF]/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest">
            <MonitorIcon className="w-3.5 h-3.5" /> Abrir Telão Full Screen
          </a>
        </div>

        <Card className="lg:ml-auto bg-[#14151A] border-border rounded-none p-4 flex items-center gap-5 border-l-4 border-l-[#34C759]" data-testid="card-pessoas-na-base">
          <UsersIcon className="w-8 h-8 text-[#34C759]" />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Pessoas na Base</div>
            <div className="font-display text-5xl font-black text-[#34C759] leading-none" data-testid="contador-dentro">{stats.dentro}</div>
          </div>
          <div className="border-l border-border pl-5 text-xs space-y-1">
            <div><span className="text-[#00E5FF] font-bold font-mono">{stats.entradas_hoje}</span> <span className="text-muted-foreground">entradas hoje</span></div>
            <div><span className="text-[#FF9F0A] font-bold font-mono">{stats.saidas_hoje}</span> <span className="text-muted-foreground">saídas hoje</span></div>
          </div>
        </Card>

        {/* LEITOR DE CÓDIGO */}
        <Card className="bg-[#14151A] border-border rounded-none p-4 border-l-4 border-l-[#FFD60A] lg:w-[380px]" data-testid="card-scanner">
          <form onSubmit={bipar}>
            <Label className="text-[10px] uppercase tracking-widest text-[#FFD60A] flex items-center gap-2">
              <ScanLineIcon className="w-4 h-4" /> Bipar Crachá / Código de Barras
            </Label>
            <Input
              ref={scanRef}
              autoFocus
              value={scanCode}
              onChange={(e) => setScanCode(e.target.value)}
              placeholder="Aproxime o leitor ou digite + Enter"
              data-testid="scanner-input"
              className="mt-2 rounded-none bg-[#0A0A0C] border-[#FFD60A]/50 focus-visible:ring-[#FFD60A] focus-visible:ring-offset-0 h-12 font-mono text-lg"
            />
          </form>
          {scanFeedback && !scanFeedback.notfound && (
            <div data-testid="scan-feedback" className={`mt-2 flex items-center gap-2 text-sm font-bold px-3 py-2 border ${scanFeedback.acao === "ENTRADA" ? "text-[#34C759] border-[#34C759]/40 bg-[#34C759]/10" : "text-[#FF9F0A] border-[#FF9F0A]/40 bg-[#FF9F0A]/10"}`}>
              {scanFeedback.acao === "ENTRADA" ? <LogInIcon className="w-4 h-4" /> : <LogOutIcon className="w-4 h-4" />}
              {scanFeedback.acao}: {scanFeedback.nome}
            </div>
          )}
        </Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#0d0d10] border border-border rounded-none h-12 p-1">
          <TabsTrigger value="controle" data-testid="tab-controle" className="rounded-none data-[state=active]:bg-[#FFD60A] data-[state=active]:text-black h-full px-6 font-bold uppercase tracking-wider text-xs">
            <UsersIcon className="w-4 h-4 mr-2" /> Na Base ({stats.dentro})
          </TabsTrigger>
          <TabsTrigger value="pessoas" data-testid="tab-pessoas" className="rounded-none data-[state=active]:bg-[#00E5FF] data-[state=active]:text-black h-full px-6 font-bold uppercase tracking-wider text-xs">
            <UserPlusIcon className="w-4 h-4 mr-2" /> Pessoas ({pessoas.length})
          </TabsTrigger>
          <TabsTrigger value="registros" data-testid="tab-registros" className="rounded-none data-[state=active]:bg-[#34C759] data-[state=active]:text-black h-full px-6 font-bold uppercase tracking-wider text-xs">
            <HistoryIcon className="w-4 h-4 mr-2" /> Registros
          </TabsTrigger>
        </TabsList>

        {/* ============ ABA: NA BASE ============ */}
        <TabsContent value="controle" className="mt-4 space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => { setTipoManual(false); setEntradaForm({ ...emptyEntrada }); setEntradaBusca(""); setAguardandoSel(null); setEntradaOpen(true); }} data-testid="btn-entrada-manual" className="rounded-none bg-[#34C759] text-black hover:bg-[#34C759]/85 font-bold uppercase tracking-wider text-xs h-10">
              <LogInIcon className="w-4 h-4 mr-2" /> Entrada Manual
            </Button>
            <Button onClick={() => { setSaidaOpen(true); }} data-testid="btn-saida-manual" variant="outline" className="rounded-none border-[#FF9F0A] text-[#FF9F0A] hover:bg-[#FF9F0A]/10 font-bold uppercase tracking-wider text-xs h-10">
              <LogOutIcon className="w-4 h-4 mr-2" /> Registrar Saída
            </Button>
          </div>

          {filaFora.length > 0 && (
            <Card className="bg-[#14151A] border-[#00E5FF]/40 rounded-none overflow-x-auto" data-testid="card-fila-espera">
              <div className="p-4 border-b border-border text-[11px] uppercase tracking-[0.3em] text-[#00E5FF] flex items-center gap-2">
                <ClockIcon className="w-4 h-4" /> Fila de Espera — chegaram e aguardam entrada ({filaFora.length})
              </div>
              <table className="w-full text-sm" data-testid="tabela-aguardando">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
                    <th className="px-4 py-2">Nome</th>
                    <th className="px-4 py-2">Empresa</th>
                    <th className="px-4 py-2">Placas</th>
                    <th className="px-4 py-2">Finalidade</th>
                    <th className="px-4 py-2">Chegou</th>
                    <th className="px-4 py-2">Crachá</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {filaFora.map((a) => (
                    <tr key={a.id} className="border-b border-border/50 hover:bg-[#0A0A0C]" data-testid={`aguardando-row-${a.id}`}>
                      <td className="px-4 py-2 font-semibold text-white">{a.nome}</td>
                      <td className="px-4 py-2 text-xs">{a.empresa || "—"}</td>
                      <td className="px-4 py-2 font-mono text-[11px] text-[#FFD60A]">{a.placa_cavalo || "—"} / {a.placa_carreta || "—"}{a.frota && <span className="text-[#00E5FF]"> · FROTA {a.frota}</span>}</td>
                      <td className="px-4 py-2">
                        <div className="flex gap-1 flex-wrap">
                          {(a.finalidades || [a.finalidade]).map((f) => (
                            <Badge key={f} className={`rounded-none text-[10px] border ${f === "DESCARREGAR_CHEIOS" ? "bg-[#00E5FF]/10 text-[#00E5FF] border-[#00E5FF]/40" : "bg-[#FFD60A]/10 text-[#FFD60A] border-[#FFD60A]/40"}`}>
                              {FINALIDADES[f] || f}
                            </Badge>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-2 font-mono text-xs">{fmtDT(a.chegou_em)}</td>
                      <td className="px-4 py-2 font-mono text-xs text-[#FF9F0A]">{a.chave_temporaria || "—"}</td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        <Button size="sm" onClick={() => abrirEntradaDaFila(a, pessoas.find((p) => p.id === a.pessoa_id))} data-testid={`btn-entrar-fila-${a.id}`}
                                className="rounded-none bg-[#34C759] text-black hover:bg-[#34C759]/85 h-7 text-[10px] font-bold uppercase mr-2">
                          <LogInIcon className="w-3 h-3 mr-1" /> Entrar
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => cancelarEspera(a)} data-testid={`btn-cancelar-fila-${a.id}`}
                                className="rounded-none text-[#FF3B30] hover:bg-[#FF3B30]/10 h-7 text-[10px] font-bold uppercase">
                          <XCircleIcon className="w-3 h-3 mr-1" /> Cancelar
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}

          <Card className="bg-[#14151A] border-border rounded-none overflow-x-auto">
            <div className="p-4 border-b border-border text-[11px] uppercase tracking-[0.3em] text-[#00E5FF]">Pessoas atualmente na base</div>
            <table className="w-full text-sm" data-testid="tabela-dentro">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
                  <th className="px-4 py-2">Nome</th>
                  <th className="px-4 py-2">CPF</th>
                  <th className="px-4 py-2">Empresa</th>
                  <th className="px-4 py-2">Telefone</th>
                  <th className="px-4 py-2">Chegada</th>
                  <th className="px-4 py-2">Entrada</th>
                  <th className="px-4 py-2">Crachá</th>
                  <th className="px-4 py-2">Placas</th>
                  <th className="px-4 py-2">Briefing</th>
                  <th className="px-4 py-2">Motivo</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {dentro.length === 0 && (
                  <tr><td colSpan={11} className="px-4 py-8 text-center text-muted-foreground text-xs">Ninguém na base no momento.</td></tr>
                )}
                {dentro.map((r) => (
                  <tr key={r.id} className="border-b border-border/50 hover:bg-[#0A0A0C]" data-testid={`dentro-row-${r.id}`}>
                    <td className="px-4 py-2 font-semibold text-white">{r.nome}</td>
                    <td className="px-4 py-2 font-mono text-xs">{r.cpf || "—"}</td>
                    <td className="px-4 py-2 text-xs">{r.empresa || "—"}</td>
                    <td className="px-4 py-2 font-mono text-xs">{r.telefone || "—"}</td>
                    <td className="px-4 py-2 font-mono text-xs text-[#FFD60A]" data-testid={`chegada-dentro-${r.id}`}>{r.chegou_em ? fmtDT(r.chegou_em) : <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-4 py-2 font-mono text-xs text-[#34C759]">{fmtDT(r.hora_entrada)}</td>
                    <td className="px-4 py-2">
                      {r.cracha_temporario
                        ? <Badge className="rounded-none bg-[#FF9F0A]/15 text-[#FF9F0A] border border-[#FF9F0A]/40 text-[10px] font-mono font-bold" data-testid={`cracha-temp-${r.id}`}>{r.cracha_temporario} · PROV.</Badge>
                        : r.chave
                          ? <span className="font-mono text-xs text-[#FFD60A]">{r.chave}</span>
                          : <span className="text-muted-foreground text-xs">—</span>}
                    </td>
                    <td className="px-4 py-2 font-mono text-[11px]">
                      {r.placa_cavalo || r.placa_carreta ? `${r.placa_cavalo || "—"} / ${r.placa_carreta || "—"}` : "—"}
                      {r.frota && <span className="text-[#00E5FF]"> · FROTA {r.frota}</span>}
                    </td>
                    <td className="px-4 py-2">
                      {r.briefing == null ? <span className="text-muted-foreground text-xs">—</span> :
                        r.briefing ? <CheckCircle2Icon className="w-4 h-4 text-[#34C759]" /> : <XCircleIcon className="w-4 h-4 text-[#FF3B30]" />}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex flex-col gap-1 items-start">
                        <Badge className="rounded-none bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/30 text-[10px]">{r.motivo_entrada}</Badge>
                        {r.operacao && <EtapaBadge op={r.operacao} testid={`etapa-dentro-${r.id}`} />}
                        {(r.operacao?.destino || r.destino) && <span className="text-[10px] font-mono font-bold text-[#34C759]" data-testid={`destino-dentro-${r.id}`}>→ {r.operacao?.destino || r.destino}</span>}
                        {r.pesagem_aberta && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 border border-[#FF9F0A]/60 text-[#FF9F0A] bg-[#FF9F0A]/10" data-testid={`pesagem-pendente-${r.id}`} title="Saída bloqueada até a 2ª pesagem na balança">Balança: 2ª pesagem pendente · {r.pesagem_aberta.ticket_str}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex justify-end items-center gap-2 flex-wrap">
                        <EtapaActions op={r.operacao} onDone={load} compact />
                        <Button size="sm" variant="outline" onClick={() => abrirSaidaPara(r)} data-testid={`btn-saida-${r.id}`} className="rounded-none border-[#FF9F0A] text-[#FF9F0A] hover:bg-[#FF9F0A]/10 h-7 text-[10px] font-bold uppercase">
                          <LogOutIcon className="w-3 h-3 mr-1" /> Saída
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          <HistoricoVeiculos />
        </TabsContent>

        {/* ============ ABA: PESSOAS ============ */}
        <TabsContent value="pessoas" className="mt-4 space-y-4">
          <div className="flex flex-wrap gap-2 items-center">
            <Button onClick={() => { setCadForm(emptyPessoa); setCadCrachaProv(""); setEditingPessoa(null); setCadOpen(true); }} data-testid="btn-novo-cadastro" className="rounded-none bg-[#00E5FF] text-black hover:bg-[#00E5FF]/85 font-bold uppercase tracking-wider text-xs h-10">
              <UserPlusIcon className="w-4 h-4 mr-2" /> Novo Cadastro
            </Button>
            <label className="rounded-none border border-[#FFD60A] text-[#FFD60A] hover:bg-[#FFD60A]/10 h-10 px-4 font-bold uppercase tracking-wider text-xs flex items-center gap-2 cursor-pointer">
              <UploadIcon className="w-4 h-4" /> Importar Planilha (CSV/Excel)
              <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={(e) => selectFile(e.target.files[0])} className="hidden" data-testid="input-import-file" />
            </label>
            <Button onClick={baixarTemplate} variant="outline" data-testid="btn-template" className="rounded-none border-border hover:border-[#00E5FF] hover:text-[#00E5FF] font-bold uppercase tracking-wider text-xs h-10">
              <DownloadIcon className="w-4 h-4 mr-2" /> Baixar Planilha Modelo
            </Button>
            {user?.role === "admin" && (
              <Button onClick={() => { setWipeConfirm(""); setWipeRegistros(false); setWipeOpen(true); }} data-testid="btn-excluir-todos" variant="outline" className="rounded-none border-[#FF3B30]/60 text-[#FF3B30] hover:bg-[#FF3B30]/10 hover:text-[#FF3B30] font-bold uppercase tracking-wider text-xs h-10">
                <Trash2Icon className="w-4 h-4 mr-2" /> Excluir Todos
              </Button>
            )}
            <Input value={searchPessoa} onChange={(e) => setSearchPessoa(e.target.value)} placeholder="Buscar nome, CPF, empresa..." data-testid="search-pessoas" className="rounded-none bg-[#0A0A0C] border-border h-10 md:w-64 md:ml-auto" />
          </div>

          {pendingFile && (
            <Card className="bg-[#14151A] border-[#FFD60A]/40 rounded-none p-4 flex items-center gap-3 flex-wrap" data-testid="pending-file-card">
              <FileSpreadsheetIcon className="w-6 h-6 text-[#FFD60A]" />
              <div className="flex-1 min-w-[160px]">
                <div className="text-sm font-semibold text-white">{pendingFile.name}</div>
                <div className="text-[10px] text-muted-foreground font-mono">{(pendingFile.size / 1024).toFixed(1)} KB</div>
              </div>
              <Button size="sm" onClick={importar} disabled={uploading} data-testid="btn-confirmar-import" className="rounded-none bg-[#FFD60A] text-black hover:bg-[#ffdf3a] font-bold uppercase tracking-wider text-[10px] h-9">
                <CheckCircle2Icon className="w-3 h-3 mr-1" /> {uploading ? "Importando..." : "Confirmar Importação"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setPendingFile(null); if (fileRef.current) fileRef.current.value = ""; }} data-testid="btn-cancelar-import" className="text-[#FF3B30] hover:text-[#FF3B30] h-9">
                <Trash2Icon className="w-4 h-4" />
              </Button>
            </Card>
          )}

          <Card className="bg-[#14151A] border-border rounded-none overflow-x-auto">
            <table className="w-full text-sm" data-testid="tabela-pessoas">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
                  <th className="px-4 py-2">Nome</th>
                  <th className="px-4 py-2">CPF</th>
                  <th className="px-4 py-2">Matrícula</th>
                  <th className="px-4 py-2">Tipo</th>
                  <th className="px-4 py-2">Chave/Crachá</th>
                  <th className="px-4 py-2">Empresa</th>
                  <th className="px-4 py-2">Telefone</th>
                  <th className="px-4 py-2">Placas</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {pessoasFiltradas.length === 0 && (
                  <tr><td colSpan={10} className="px-4 py-8 text-center text-muted-foreground text-xs">Nenhuma pessoa cadastrada. Use "Novo Cadastro" ou importe uma planilha.</td></tr>
                )}
                {pessoasFiltradas.map((p) => (
                  <tr key={p.id} className="border-b border-border/50 hover:bg-[#0A0A0C]" data-testid={`pessoa-row-${p.id}`}>
                    <td className="px-4 py-2 font-semibold text-white">{p.nome}</td>
                    <td className="px-4 py-2 font-mono text-xs">{p.cpf || "—"}</td>
                    <td className="px-4 py-2 font-mono text-xs">{p.matricula || "—"}</td>
                    <td className="px-4 py-2">
                      {p.tipo_cracha === "ROTATIVO"
                        ? <Badge className="rounded-none bg-[#FF9F0A]/15 text-[#FF9F0A] border border-[#FF9F0A]/40 text-[9px] font-bold">ROTATIVO</Badge>
                        : <Badge className="rounded-none bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/30 text-[9px] font-bold">FIXO</Badge>}
                      <div className="text-[9px] font-bold tracking-wider text-muted-foreground mt-1" data-testid={`tipo-pessoa-${p.id}`}>{tipoPessoaAuto(p)}</div>
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-[#FFD60A]">
                      {p.tipo_cracha === "ROTATIVO"
                        ? ((p.cracha_atual || p.chave)
                            ? <span className="text-[#FF9F0A]" data-testid={`cracha-posse-${p.id}`}>{p.cracha_atual || p.chave} {p.status === "DENTRO" ? "(em uso)" : "(em posse · não devolvido)"}</span>
                            : <span className="text-muted-foreground">bipar na entrada</span>)
                        : (p.chave || "—")}
                    </td>
                    <td className="px-4 py-2 text-xs">{p.empresa || "—"}</td>
                    <td className="px-4 py-2 font-mono text-xs">{p.telefone || "—"}</td>
                    <td className="px-4 py-2 font-mono text-[11px]">{p.placa_cavalo || p.placa_carreta ? `${p.placa_cavalo || "—"} / ${p.placa_carreta || "—"}` : "—"}{p.frota && <span className="text-[#00E5FF]"> · FROTA {p.frota}</span>}</td>
                    <td className="px-4 py-2">
                      {p.status === "DENTRO"
                        ? <Badge className="rounded-none bg-[#34C759]/15 text-[#34C759] border border-[#34C759]/40 text-[10px] font-bold">DENTRO DA BASE</Badge>
                        : <Badge className="rounded-none bg-muted/20 text-muted-foreground border border-border text-[10px]">FORA</Badge>}
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      {p.tipo_cracha === "ROTATIVO" && p.status !== "DENTRO" && (p.cracha_atual || p.chave) && (
                        <Button size="sm" variant="outline" onClick={() => devolverCracha(p)} data-testid={`devolver-cracha-${p.id}`}
                                className="h-7 px-2 mr-1 rounded-none border-[#FF9F0A]/60 text-[#FF9F0A] hover:bg-[#FF9F0A]/10 text-[10px] font-bold uppercase">
                          Devolveu crachá
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => { setEditingPessoa(p); setCadForm({ ...emptyPessoa, ...Object.fromEntries(Object.keys(emptyPessoa).map((k) => [k, p[k] || (k === "tipo_cracha" ? "FIXO" : k === "tipo_pessoa" ? tipoPessoaAuto(p) : "")])) }); setCadOpen(true); }} data-testid={`edit-pessoa-${p.id}`} className="h-7 w-7 p-0 text-muted-foreground hover:text-[#FFD60A]">
                        <EditIcon className="w-3 h-3" />
                      </Button>
                      {canDelete && (
                        <Button size="sm" variant="ghost" onClick={() => excluirPessoa(p)} data-testid={`del-pessoa-${p.id}`} className="h-7 w-7 p-0 text-muted-foreground hover:text-[#FF3B30]">
                          <Trash2Icon className="w-3 h-3" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        {/* ============ ABA: REGISTROS ============ */}
        <TabsContent value="registros" className="mt-4 space-y-4">
          <div className="flex flex-wrap gap-2 items-center">
            <div className="flex border border-border" data-testid="registros-subtabs">
              <button
                onClick={() => setRegView("hoje")}
                data-testid="subtab-hoje"
                className={`px-4 h-10 text-xs font-bold uppercase tracking-wider ${regView === "hoje" ? "bg-[#34C759] text-black" : "bg-[#0d0d10] text-muted-foreground hover:text-white"}`}
              >
                Registros do Dia ({registrosHoje.length})
              </button>
              <button
                onClick={() => setRegView("historico")}
                data-testid="subtab-historico"
                className={`px-4 h-10 text-xs font-bold uppercase tracking-wider ${regView === "historico" ? "bg-[#00E5FF] text-black" : "bg-[#0d0d10] text-muted-foreground hover:text-white"}`}
              >
                Histórico Completo
              </button>
            </div>

            {regView === "historico" && (
              <div className="flex flex-wrap gap-2 items-center" data-testid="hist-filtros">
                <Select value={histFiltroTipo} onValueChange={setHistFiltroTipo}>
                  <SelectTrigger data-testid="hist-filtro-tipo" className="rounded-none bg-[#0A0A0C] h-10 border-border w-40"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#14151A] border-border rounded-none">
                    <SelectItem value="todos">Todos os registros</SelectItem>
                    <SelectItem value="data">Filtrar por Data</SelectItem>
                    <SelectItem value="mes">Filtrar por Mês</SelectItem>
                    <SelectItem value="ano">Filtrar por Ano</SelectItem>
                  </SelectContent>
                </Select>
                {histFiltroTipo === "data" && (
                  <Input type="date" value={histData} onChange={(e) => setHistData(e.target.value)} data-testid="hist-filtro-data" className="rounded-none bg-[#0A0A0C] border-border h-10 w-44 font-mono" />
                )}
                {histFiltroTipo === "mes" && (
                  <Input type="month" value={histMes} onChange={(e) => setHistMes(e.target.value)} data-testid="hist-filtro-mes" className="rounded-none bg-[#0A0A0C] border-border h-10 w-44 font-mono" />
                )}
                {histFiltroTipo === "ano" && (
                  <Input type="number" min="2020" max="2100" value={histAno} onChange={(e) => setHistAno(e.target.value)} data-testid="hist-filtro-ano" className="rounded-none bg-[#0A0A0C] border-border h-10 w-28 font-mono" placeholder="2026" />
                )}
                {histLoading && <span className="text-xs text-muted-foreground animate-pulse">Carregando...</span>}
              </div>
            )}

            <Input value={searchReg} onChange={(e) => setSearchReg(e.target.value)} placeholder="Buscar registros..." data-testid="search-registros" className="rounded-none bg-[#0A0A0C] border-border h-10 md:w-56 md:ml-auto" />
          </div>

          <Card className="bg-[#14151A] border-border rounded-none overflow-x-auto">
            {regView === "historico" && (
              <div className="p-3 border-b border-border text-[11px] uppercase tracking-[0.3em] text-[#00E5FF]" data-testid="hist-resumo">
                {histFiltrados.length} registro(s)
                {histFiltroTipo === "data" && histData ? ` em ${histData.split("-").reverse().join("/")}` : ""}
                {histFiltroTipo === "mes" && histMes ? ` em ${histMes.split("-").reverse().join("/")}` : ""}
                {histFiltroTipo === "ano" && histAno ? ` em ${histAno}` : ""}
              </div>
            )}
            <table className="w-full text-sm" data-testid="tabela-registros">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
                  <th className="px-4 py-2">Nome</th>
                  <th className="px-4 py-2">Empresa</th>
                  <th className="px-4 py-2">Crachá</th>
                  <th className="px-4 py-2">Entrada</th>
                  <th className="px-4 py-2">Motivo Entrada</th>
                  <th className="px-4 py-2">Saída</th>
                  <th className="px-4 py-2">Motivo Saída</th>
                  <th className="px-4 py-2">Permanência</th>
                  <th className="px-4 py-2">TQ</th>
                  <th className="px-4 py-2">RF</th>
                  <th className="px-4 py-2">Cesta</th>
                  <th className="px-4 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {(regView === "hoje" ? registrosHoje : histFiltrados).length === 0 && (
                  <tr><td colSpan={12} className="px-4 py-8 text-center text-muted-foreground text-xs">
                    {regView === "hoje" ? "Nenhum registro hoje." : "Nenhum registro para o filtro selecionado."}
                  </td></tr>
                )}
                {(regView === "hoje" ? registrosHoje : histFiltrados).map((r) => (
                  <tr key={r.id} className="border-b border-border/50 hover:bg-[#0A0A0C]" data-testid={`registro-row-${r.id}`}>
                    <td className="px-4 py-2 font-semibold text-white">{r.nome}</td>
                    <td className="px-4 py-2 text-xs">{r.empresa || "—"}</td>
                    <td className="px-4 py-2">
                      {r.cracha_temporario
                        ? <span className="font-mono text-[11px] text-[#FF9F0A]">{r.cracha_temporario} (prov.{r.cracha_devolvido ? " · devolvido" : ""})</span>
                        : r.chave
                          ? <span className="font-mono text-[11px] text-[#FFD60A]">{r.chave}</span>
                          : <span className="text-muted-foreground text-xs">—</span>}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-[#34C759]">{fmtDT(r.hora_entrada)}</td>
                    <td className="px-4 py-2 text-[11px]">{r.motivo_entrada}</td>
                    <td className="px-4 py-2 font-mono text-xs text-[#FF9F0A]">{fmtDT(r.hora_saida)}</td>
                    <td className="px-4 py-2 text-[11px]">{r.motivo_saida || "—"}</td>
                    <td className="px-4 py-2 font-mono text-xs">{fmtPerm(r.permanencia_min)}</td>
                    <td className="px-4 py-2 font-mono text-xs text-center">{r.qtd_tq ?? "—"}</td>
                    <td className="px-4 py-2 font-mono text-xs text-center">{r.qtd_rf ?? "—"}</td>
                    <td className="px-4 py-2 font-mono text-xs text-center">{r.qtd_cesta ?? "—"}</td>
                    <td className="px-4 py-2">
                      {r.status === "DENTRO"
                        ? <Badge className="rounded-none bg-[#34C759]/15 text-[#34C759] border border-[#34C759]/40 text-[10px] font-bold">DENTRO</Badge>
                        : <Badge className="rounded-none bg-muted/20 text-muted-foreground border border-border text-[10px]">CONCLUÍDO</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* MODAL: EXCLUIR TODOS (ADMIN) */}
      <Dialog open={wipeOpen} onOpenChange={(v) => { setWipeOpen(v); if (!v) { setWipeConfirm(""); setWipeRegistros(false); } }}>
        <DialogContent className="bg-[#14151A] border-[#FF3B30]/50 rounded-none max-w-md" data-testid="modal-excluir-todos">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wider text-[#FF3B30]">Excluir TODOS os cadastros</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-white border border-[#FF3B30]/40 bg-[#FF3B30]/5 p-3" data-testid="wipe-warning">
              Esta ação apaga <b>{pessoas.length} cadastro(s) de pessoas</b> de uma só vez e <b>não pode ser desfeita</b>.
            </div>
            <label className="flex items-center gap-2 text-sm text-white cursor-pointer select-none">
              <input type="checkbox" checked={wipeRegistros} onChange={(e) => setWipeRegistros(e.target.checked)} data-testid="wipe-incluir-registros" className="accent-[#FF3B30] w-4 h-4" />
              Apagar também todo o histórico de entradas/saídas
            </label>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Digite <span className="text-[#FF3B30] font-bold">APAGAR</span> para confirmar</Label>
              <Input data-testid="wipe-confirm-input" value={wipeConfirm} onChange={(e) => setWipeConfirm(e.target.value)} className="mt-1 rounded-none bg-[#0A0A0C] border-[#FF3B30]/40 h-10 font-mono" placeholder="APAGAR" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setWipeOpen(false)} data-testid="wipe-cancelar" className="rounded-none uppercase tracking-wider text-xs">Cancelar</Button>
            <Button onClick={excluirTodos} disabled={wiping || wipeConfirm.trim().toUpperCase() !== "APAGAR"} data-testid="wipe-confirmar" className="rounded-none bg-[#FF3B30] text-white hover:bg-[#FF3B30]/85 font-bold uppercase tracking-wider text-xs">
              {wiping ? "Excluindo..." : "Excluir Tudo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MODAL: NOVO CADASTRO / EDITAR */}
      <Dialog open={cadOpen} onOpenChange={(v) => { setCadOpen(v); if (!v) { setEditingPessoa(null); setCadForm(emptyPessoa); } }}>
        <DialogContent className="bg-[#14151A] border-border rounded-none max-w-2xl" data-testid="modal-cadastro">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wider">{editingPessoa ? "Editar Cadastro" : "Novo Cadastro"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Nome Completo *</Label>
              <Input data-testid="cad-nome" value={cadForm.nome} onChange={(e) => setCadForm({ ...cadForm, nome: e.target.value })} className={inputCls} />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">CPF</Label>
              <Input data-testid="cad-cpf" value={cadForm.cpf} onChange={(e) => setCadForm({ ...cadForm, cpf: e.target.value })} className={`${inputCls} font-mono ${cpfDuplicado ? "border-[#FF3B30]" : ""}`} placeholder="000.000.000-00" />
              {cpfDuplicado && (
                <div className="mt-1 border border-[#FF3B30]/50 bg-[#FF3B30]/10 px-2 py-1.5 text-[10px] text-[#FF3B30] flex items-center justify-between gap-2" data-testid="cad-cpf-duplicado">
                  <span><b>{cpfDuplicado.nome}</b> já está cadastrado(a) com este CPF.</span>
                  <button type="button" onClick={() => { setCadOpen(false); setTab("pessoas"); setSearchPessoa(cpfDuplicado.cpf || cpfDuplicado.nome); }}
                          data-testid="cad-ver-cadastro-existente" className="underline font-bold whitespace-nowrap">Ver cadastro</button>
                </div>
              )}
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Matrícula</Label>
              <Input data-testid="cad-matricula" value={cadForm.matricula} onChange={(e) => setCadForm({ ...cadForm, matricula: e.target.value })} className={`${inputCls} font-mono`} />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Tipo de Pessoa</Label>
              <Select value={cadForm.tipo_pessoa || "VISITANTE"} onValueChange={(v) => setCadForm({ ...cadForm, tipo_pessoa: v })}>
                <SelectTrigger data-testid="cad-tipo-pessoa" className="mt-1 rounded-none bg-[#0A0A0C] h-10 border-border"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#14151A] border-border rounded-none">
                  <SelectItem value="VISITANTE">VISITANTE — visita/serviço a pé</SelectItem>
                  <SelectItem value="MOTORISTA">MOTORISTA — entra com veículo</SelectItem>
                  <SelectItem value="COLABORADOR">COLABORADOR — funcionário da base</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Tipo de Crachá</Label>
              <Select value={cadForm.tipo_cracha} onValueChange={(v) => setCadForm({ ...cadForm, tipo_cracha: v, chave: v === "ROTATIVO" ? "" : cadForm.chave })}>
                <SelectTrigger data-testid="cad-tipo-cracha" className="mt-1 rounded-none bg-[#0A0A0C] h-10 border-border"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#14151A] border-border rounded-none">
                  <SelectItem value="FIXO">FIXO — funcionário/terceiro permanente</SelectItem>
                  <SelectItem value="ROTATIVO">ROTATIVO — motorista/visitante diário</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {cadForm.tipo_cracha === "FIXO" ? (
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-[#FFD60A]">Chave / Código do Crachá Fixo</Label>
                <Input data-testid="cad-chave" value={cadForm.chave} onChange={(e) => setCadForm({ ...cadForm, chave: e.target.value })} className={`${inputCls} font-mono border-[#FFD60A]/40`} placeholder="Bipe ou digite o código" />
              </div>
            ) : (
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-[#FF9F0A]">Nº do Crachá Provisório (entrada de agora)</Label>
                <Input data-testid="cad-cracha-prov" value={cadCrachaProv} onChange={(e) => setCadCrachaProv(e.target.value)} className={`${inputCls} font-mono border-[#FF9F0A]/60`} placeholder="Bipe ou digite o crachá" />
                <div className="text-[10px] text-[#FF9F0A] mt-1" data-testid="cad-rotativo-info">
                  Se preenchido, a ENTRADA já abre com este crachá vinculado. Será devolvido na SAÍDA.
                </div>
              </div>
            )}
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Empresa / Fornecedor</Label>
              <Input data-testid="cad-empresa" value={cadForm.empresa} onChange={(e) => setCadForm({ ...cadForm, empresa: e.target.value })} className={inputCls} />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Telefone / Zap</Label>
              <Input data-testid="cad-telefone" value={cadForm.telefone} onChange={(e) => setCadForm({ ...cadForm, telefone: e.target.value })} className={`${inputCls} font-mono`} placeholder="(00) 00000-0000" />
            </div>
            <div className="md:col-span-2 grid grid-cols-3 gap-2">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Placa Cavalo</Label>
                <Input data-testid="cad-placa-cavalo" value={cadForm.placa_cavalo} onChange={(e) => setCadForm({ ...cadForm, placa_cavalo: e.target.value.toUpperCase() })} className={`${inputCls} font-mono`} placeholder="ABC1D23" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Placa Carreta</Label>
                <Input data-testid="cad-placa-carreta" value={cadForm.placa_carreta} onChange={(e) => setCadForm({ ...cadForm, placa_carreta: e.target.value.toUpperCase() })} className={`${inputCls} font-mono`} placeholder="XYZ9E88" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Frota</Label>
                <Input data-testid="cad-frota" value={cadForm.frota || ""} onChange={(e) => setCadForm({ ...cadForm, frota: e.target.value.toUpperCase() })} className={`${inputCls} font-mono`} placeholder="Nº frota" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={salvarCadastro} data-testid="cad-salvar" className="rounded-none bg-[#00E5FF] text-black hover:bg-[#00E5FF]/85 font-bold uppercase tracking-wider">
              {editingPessoa ? "Salvar Alterações" : "Cadastrar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MODAL: ENTRADA MANUAL */}
      <Dialog open={entradaOpen} onOpenChange={(v) => { setEntradaOpen(v); if (!v) { setScanPendente(""); setAguardandoSel(null); } }}>
        <DialogContent className="bg-[#14151A] border-border rounded-none max-w-2xl" data-testid="modal-entrada">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wider flex items-center gap-2">
              <LogInIcon className="w-5 h-5 text-[#34C759]" /> Entrada Manual
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {aguardandoSel && (
              <div className="border border-[#00E5FF]/50 bg-[#00E5FF]/5 px-3 py-2 text-[11px]" data-testid="aguardando-banner">
                <span className="text-[#00E5FF] font-bold uppercase tracking-wider">Na fila de espera</span>
                <span className="text-white"> desde <b className="font-mono">{fmtDT(aguardandoSel.chegou_em)}</b> · Finalidade: <b>{finsLabel(aguardandoSel)}</b></span>
                {(aguardandoSel.placa_cavalo || aguardandoSel.placa_carreta) && (
                  <span className="font-mono text-[#FFD60A]"> · {aguardandoSel.placa_cavalo || "—"} / {aguardandoSel.placa_carreta || "—"}</span>
                )}
                <div className="text-muted-foreground mt-0.5">ENTRAR COM VEÍCULO conclui a espera · ENTRAR A PÉ (ex.: banheiro) mantém o veículo na fila.</div>
              </div>
            )}
            {scanPendente && !aguardandoSel && (
              <div className="flex items-center justify-between gap-2 border border-[#FF9F0A]/50 bg-[#FF9F0A]/5 px-3 py-2 flex-wrap" data-testid="scan-pendente-banner">
                <div className="text-[11px] text-[#FF9F0A]">
                  Crachá <span className="font-mono font-bold">{scanPendente}</span> não vinculado — busque a pessoa já cadastrada abaixo, ou:
                </div>
                <Button size="sm" onClick={() => { setEntradaOpen(false); setScanPendente(""); setCadForm({ ...emptyPessoa, tipo_cracha: "ROTATIVO" }); setCadCrachaProv(scanPendente); setEditingPessoa(null); setCadOpen(true); }} data-testid="btn-scan-novo-cadastro" className="rounded-none bg-[#FF9F0A] text-black hover:bg-[#FF9F0A]/85 h-7 font-bold uppercase tracking-wider text-[10px]">
                  <UserPlusIcon className="w-3 h-3 mr-1" /> Novo Cadastro
                </Button>
              </div>
            )}
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Pessoa Cadastrada (busque e selecione)</Label>
              {pessoaSelEntrada ? (
                <div className="mt-1 flex items-center justify-between border border-[#34C759]/50 bg-[#34C759]/5 px-3 py-2" data-testid="entrada-pessoa-selecionada">
                  <div>
                    <div className="text-sm font-bold text-white">{pessoaSelEntrada.nome}</div>
                    <div className="text-[10px] font-mono text-muted-foreground">{pessoaSelEntrada.cpf || pessoaSelEntrada.matricula || "—"} · {pessoaSelEntrada.empresa || "sem empresa"}</div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => setEntradaForm({ ...entradaForm, pessoa_id: "" })} className="text-[#FF3B30] h-7">Trocar</Button>
                </div>
              ) : (
                <>
                  <Input value={entradaBusca} onChange={(e) => setEntradaBusca(e.target.value)} placeholder="Digite nome, CPF ou matrícula..." data-testid="entrada-busca" className={inputCls} />
                  {entradaBusca && (
                    <div className="border border-border bg-[#0A0A0C] max-h-44 overflow-y-auto" data-testid="entrada-autocomplete">
                      {entradaCandidatas.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">Nenhum cadastro fora da base corresponde. Cadastre na aba Pessoas.</div>}
                      {entradaCandidatas.map((p) => (
                        <button key={p.id} onClick={() => {
                          const naFila = aguardando.find((a) => a.pessoa_id === p.id && a.status === "AGUARDANDO");
                          if (naFila) return abrirEntradaDaFila(naFila, p);
                          const tp = p.tipo_pessoa || (tipoManual ? entradaForm.tipo_pessoa : tipoPessoaAuto(p));
                          setEntradaForm({ ...entradaForm, pessoa_id: p.id, tipo_pessoa: tp, motivo: MOTIVO_PADRAO[tp], briefing: briefingAuto(p), empresa: p.empresa || "", telefone: p.telefone || "", placa_cavalo: p.placa_cavalo || "", placa_carreta: p.placa_carreta || "", frota: p.frota || "", chave_temporaria: entradaForm.chave_temporaria || p.chave || "" });
                        }} data-testid={`entrada-opt-${p.id}`} className="w-full text-left px-3 py-2 hover:bg-[#14151A] border-b border-border/40">
                          <div className="text-sm text-white">{p.nome} {p.tipo_cracha === "ROTATIVO" && <span className="text-[9px] text-[#FF9F0A] font-bold ml-1">ROTATIVO</span>}</div>
                          <div className="text-[10px] font-mono text-muted-foreground">{p.cpf || p.matricula || "—"} · {p.empresa || "—"}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Tipo de Entrada</Label>
              <div className="mt-1 grid grid-cols-3 gap-2" data-testid="entrada-tipo-pessoa">
                {TIPOS_PESSOA.map(([k, label, cor]) => (
                  <button key={k} type="button" onClick={() => { setTipoManual(true); setEntradaForm({ ...entradaForm, tipo_pessoa: k, motivo: MOTIVO_PADRAO[k] }); }}
                          data-testid={`entrada-tipo-${k}`}
                          className="h-10 border-2 text-[11px] font-bold uppercase tracking-wider transition-colors"
                          style={{ borderColor: cor, background: entradaForm.tipo_pessoa === k ? cor : "transparent", color: entradaForm.tipo_pessoa === k ? "#000" : cor }}>
                    {label}
                  </button>
                ))}
              </div>
              <div className="text-[10px] text-muted-foreground mt-1">
                {entradaForm.tipo_pessoa === "MOTORISTA" ? "Empresa, telefone, placas e fila de espera disponíveis." :
                 entradaForm.tipo_pessoa === "COLABORADOR" ? "Colaborador da base: sem veículo e sem briefing." :
                 "Visitante a pé: briefing de segurança obrigatório."}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {entradaForm.tipo_pessoa === "MOTORISTA" && (
                <>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Empresa / Fornecedor</Label>
                    <Input data-testid="entrada-empresa" value={entradaForm.empresa} onChange={(e) => setEntradaForm({ ...entradaForm, empresa: e.target.value })} className={inputCls} />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Telefone</Label>
                    <Input data-testid="entrada-telefone" value={entradaForm.telefone} onChange={(e) => setEntradaForm({ ...entradaForm, telefone: e.target.value })} className={`${inputCls} font-mono`} />
                  </div>
                  <div className="md:col-span-2 grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Placa Cavalo</Label>
                      <Input data-testid="entrada-placa-cavalo" value={entradaForm.placa_cavalo} onChange={(e) => setEntradaForm({ ...entradaForm, placa_cavalo: e.target.value.toUpperCase() })} className={`${inputCls} font-mono`} placeholder="ABC1D23" />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Placa Carreta</Label>
                      <Input data-testid="entrada-placa-carreta" value={entradaForm.placa_carreta} onChange={(e) => setEntradaForm({ ...entradaForm, placa_carreta: e.target.value.toUpperCase() })} className={`${inputCls} font-mono`} placeholder="XYZ9E88" />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Frota</Label>
                      <Input data-testid="entrada-frota" value={entradaForm.frota} onChange={(e) => setEntradaForm({ ...entradaForm, frota: e.target.value.toUpperCase() })} className={`${inputCls} font-mono`} placeholder="Nº frota" />
                    </div>
                  </div>
                </>
              )}
              <div>
                <Label className={`text-[10px] uppercase tracking-wider ${pessoaSelEntrada?.tipo_cracha === "ROTATIVO" ? "text-[#FF9F0A]" : "text-[#FFD60A]"}`}>
                  {pessoaSelEntrada?.tipo_cracha === "ROTATIVO" ? "Nº do Crachá Provisório / QR Code *" : "Crachá / Chave Temporária"}
                </Label>
                <Input data-testid="entrada-chave" value={entradaForm.chave_temporaria}
                       onChange={(e) => setEntradaForm({ ...entradaForm, chave_temporaria: e.target.value })}
                       onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); resolverCrachaNaEntrada(entradaForm.chave_temporaria); } }}
                       className={`${inputCls} font-mono ${pessoaSelEntrada?.tipo_cracha === "ROTATIVO" ? "border-[#FF9F0A]/60" : "border-[#FFD60A]/40"}`} placeholder="Bipe o crachá aqui" />
                {pessoaSelEntrada?.tipo_cracha === "ROTATIVO" && (
                  <div className="text-[10px] text-[#FF9F0A] mt-1" data-testid="entrada-rotativo-hint">Obrigatório: o crachá fica vinculado à pessoa (entra/sai bipando) até ser marcado como DEVOLVIDO.</div>
                )}
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Motivo da Entrada</Label>
                <Select value={entradaForm.motivo} onValueChange={(v) => setEntradaForm({ ...entradaForm, motivo: v })}>
                  <SelectTrigger data-testid="entrada-motivo" className="mt-1 rounded-none bg-[#0A0A0C] h-10 border-border"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#14151A] border-border rounded-none">
                    {MOTIVOS_ENTRADA.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {entradaForm.tipo_pessoa !== "COLABORADOR" && (
            <button
              type="button"
              onClick={() => setEntradaForm({ ...entradaForm, briefing: !entradaForm.briefing })}
              data-testid="entrada-briefing"
              className={`w-full flex items-center gap-4 border-2 p-3 text-left transition-colors cursor-pointer ${
                entradaForm.briefing ? "border-[#34C759] bg-[#34C759]/10" : "border-border bg-[#0A0A0C] hover:border-[#34C759]/50"
              }`}
            >
              <div className={`w-7 h-7 shrink-0 border-2 flex items-center justify-center ${
                entradaForm.briefing ? "border-[#34C759] bg-[#34C759]" : "border-muted-foreground/50"
              }`}>
                {entradaForm.briefing && <CheckCircle2Icon className="w-5 h-5 text-black" strokeWidth={3} />}
              </div>
              <div>
                <div className={`text-sm font-bold ${entradaForm.briefing ? "text-[#34C759]" : ""}`}>
                  Briefing Realizado {entradaForm.briefing ? "✓ SIM" : "— NÃO"}
                </div>
                <div className="text-[10px] text-muted-foreground">Instruções de segurança da base foram repassadas? Toque/clique para alternar.</div>
                {(() => {
                  const st = briefingStatus(pessoaSelEntrada);
                  if (st.estado === "SEM") return pessoaSelEntrada ? <div className="text-[10px] text-muted-foreground mt-0.5" data-testid="briefing-validade">Primeiro briefing — vale 6 meses a partir de hoje.</div> : null;
                  if (st.estado === "VALIDO") return <div className="text-[10px] text-[#34C759] mt-0.5 font-semibold" data-testid="briefing-validade">Briefing em dia · válido até {fmtData(st.validoAte)} ({st.dias} dias) — marcado automaticamente.</div>;
                  if (st.estado === "VENCENDO") return <div className="text-[10px] text-[#FF9F0A] mt-0.5 font-bold" data-testid="briefing-validade">Briefing VENCENDO em {st.dias} dia(s) ({fmtData(st.validoAte)}) — refazer o briefing e marcar.</div>;
                  return <div className="text-[10px] text-[#FF3B30] mt-0.5 font-bold" data-testid="briefing-validade">Briefing VENCIDO em {fmtData(st.validoAte)} — refazer o briefing e marcar.</div>;
                })()}
              </div>
            </button>
            )}
          </div>
          {!aguardandoSel && entradaForm.tipo_pessoa !== "MOTORISTA" && (
            <div className="border border-[#FFD60A]/30 bg-[#FFD60A]/5 px-3 py-2 text-[10px] text-[#FFD60A]" data-testid="dica-motorista-fila">
              É motorista? Selecione <b>MOTORISTA</b> acima para informar placas e usar a FILA DE ESPERA (descarregar cheios / carregar vazios / descarregar backload).
            </div>
          )}
          {!aguardandoSel && entradaForm.tipo_pessoa === "MOTORISTA" && (
            <div className="border border-[#FFD60A]/40 bg-[#FFD60A]/5 p-3 space-y-2" data-testid="secao-chegada">
              <div className="text-[10px] uppercase tracking-wider text-[#FFD60A] font-bold flex items-center gap-1.5">
                <ClockIcon className="w-3.5 h-3.5" /> Chegou com veículo — o que a carreta vai fazer?
              </div>
              <div className="text-[10px] text-muted-foreground">Marque a(s) finalidade(s) e escolha: <b className="text-white">Colocar na fila</b> (aguarda do lado de fora, nome + placa + frota no telão) ou <b className="text-white">Entrar com veículo</b> (entra direto e já inicia a operação). Pode marcar mais de uma: descarrega os cheios, carrega os vazios, descarrega o backload — ou chegou com a carreta vazia e fica em standby.</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {FINALIDADES_FILA.map((k) => {
                  const cor = FIN_COR[k];
                  return (
                    <button key={k} type="button" onClick={() => setChegadaFins({ ...chegadaFins, [k]: !chegadaFins[k] })}
                            data-testid={`chegada-fin-${k}`}
                            className={`flex items-center gap-2 border-2 px-3 h-9 text-[10px] font-bold uppercase tracking-wider transition-colors ${chegadaFins[k] ? "text-black" : "bg-transparent hover:bg-white/5"}`}
                            style={{ borderColor: cor, background: chegadaFins[k] ? cor : undefined, color: chegadaFins[k] ? "#000" : cor }}>
                      <span className={`w-4 h-4 shrink-0 border-2 flex items-center justify-center ${chegadaFins[k] ? "border-black bg-black/20" : ""}`} style={{ borderColor: chegadaFins[k] ? "#000" : cor }}>
                        {chegadaFins[k] && <CheckCircle2Icon className="w-3 h-3" strokeWidth={3} />}
                      </span>
                      {FINALIDADES[k]}
                    </button>
                  );
                })}
              </div>
              {entradaForm.frota && chegadaFins.CARREGAR_LOAD && <DestinoLoad testid="chegada-destino" />}
              <Button size="sm" onClick={marcarChegada} data-testid="btn-aguardar-fila"
                      className="w-full rounded-none bg-[#FFD60A] text-black hover:bg-[#FFD60A]/85 h-9 text-[10px] font-bold uppercase tracking-wider">
                <ClockIcon className="w-3.5 h-3.5 mr-1" /> Colocar na fila
              </Button>
            </div>
          )}
          <DialogFooter>
            {aguardandoSel ? (
              <div className="w-full space-y-2">
                {emStandby(aguardandoSel) && (
                  <div className="border border-[#34C759]/50 bg-[#34C759]/5 p-3 space-y-2" data-testid="secao-standby-destino">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-[#34C759] flex items-center gap-2">
                      <TruckIcon className="w-4 h-4" /> Frota em standby — o que vai fazer agora?
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {["CARREGAR_LOAD", "CARREGAR_VAZIOS"].map((k) => {
                        const cor = FIN_COR[k];
                        return (
                          <button key={k} type="button" onClick={() => setStandbyFins({ ...standbyFins, [k]: !standbyFins[k] })}
                                  data-testid={`standby-fin-${k}`}
                                  className={`flex items-center gap-2 border-2 px-3 h-10 text-[11px] font-bold uppercase tracking-wider transition-colors ${standbyFins[k] ? "text-black" : "bg-transparent hover:bg-white/5"}`}
                                  style={{ borderColor: cor, background: standbyFins[k] ? cor : undefined, color: standbyFins[k] ? "#000" : cor }}>
                            <span className={`w-4 h-4 shrink-0 border-2 flex items-center justify-center ${standbyFins[k] ? "border-black bg-black/20" : ""}`} style={{ borderColor: standbyFins[k] ? "#000" : cor }}>
                              {standbyFins[k] && <CheckCircle2Icon className="w-3 h-3" strokeWidth={3} />}
                            </span>
                            {FINALIDADES[k]}
                          </button>
                        );
                      })}
                    </div>
                    {entradaForm.frota && standbyFins.CARREGAR_LOAD && <DestinoLoad testid="standby-destino" />}
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full" data-testid="entrada-fila-acoes">
                <Button onClick={() => registrarEntrada({ semVeiculo: true, motivo: "PEDESTRE — VEÍCULO NA FILA" })} data-testid="entrada-a-pe"
                        variant="outline" className="rounded-none border-[#00E5FF] text-[#00E5FF] hover:bg-[#00E5FF]/10 font-bold uppercase tracking-wider text-xs w-full h-auto min-h-10 py-2 whitespace-normal">
                  <FootprintsIcon className="w-4 h-4 mr-2 shrink-0" /> Entrar a Pé (mantém na fila)
                </Button>
                <Button onClick={() => registrarEntrada({ finalizar: true, motivo: finsLabel(aguardandoSel) || "CARGA/DESCARGA" })} data-testid="entrada-com-veiculo"
                        className="rounded-none bg-[#34C759] text-black hover:bg-[#34C759]/85 font-bold uppercase tracking-wider text-xs w-full h-auto min-h-10 py-2 whitespace-normal">
                  <TruckIcon className="w-4 h-4 mr-2 shrink-0" /> Entrar com Veículo · {emStandby(aguardandoSel)
                    ? (Object.keys(standbyFins).filter((k) => standbyFins[k]).map((f) => FINALIDADES[f]).join(" + ") || "escolha acima")
                    : finsLabel(aguardandoSel)}
                </Button>
                </div>
              </div>
            ) : entradaForm.tipo_pessoa === "MOTORISTA" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full" data-testid="entrada-direta-acoes">
                <Button onClick={() => registrarEntrada()} data-testid="entrada-confirmar" variant="outline"
                        className="rounded-none border-[#00E5FF] text-[#00E5FF] hover:bg-[#00E5FF]/10 font-bold uppercase tracking-wider text-xs w-full h-auto min-h-10 py-2 whitespace-normal">
                  <FootprintsIcon className="w-4 h-4 mr-2 shrink-0" /> Registrar Entrada (a pé)
                </Button>
                <Button onClick={() => registrarEntrada({ finalizar: true, direto: true })} data-testid="entrada-direta-com-veiculo"
                        className="rounded-none bg-[#34C759] text-black hover:bg-[#34C759]/85 font-bold uppercase tracking-wider text-xs w-full h-auto min-h-10 py-2 whitespace-normal">
                  <TruckIcon className="w-4 h-4 mr-2 shrink-0" /> Entrar com Veículo · {Object.keys(chegadaFins).filter((k) => chegadaFins[k] && k !== "CARRETA_VAZIA_STANDBY").map((f) => FINALIDADES[f]).join(" + ") || "marque a finalidade"}
                </Button>
              </div>
            ) : (
              <Button onClick={() => registrarEntrada()} data-testid="entrada-confirmar" className="rounded-none bg-[#34C759] text-black hover:bg-[#34C759]/85 font-bold uppercase tracking-wider">
                <LogInIcon className="w-4 h-4 mr-2" /> Registrar Entrada
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MODAL: SAÍDA MANUAL */}
      <Dialog open={saidaOpen} onOpenChange={setSaidaOpen}>
        <DialogContent className="bg-[#14151A] border-border rounded-none max-w-lg" data-testid="modal-saida">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wider flex items-center gap-2">
              <LogOutIcon className="w-5 h-5 text-[#FF9F0A]" /> Registrar Saída
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Colaborador / Visitante (na base)</Label>
              <Select value={saidaForm.pessoa_id} onValueChange={(v) => setSaidaForm({ ...saidaForm, pessoa_id: v })}>
                <SelectTrigger data-testid="saida-pessoa" className="mt-1 rounded-none bg-[#0A0A0C] h-10 border-border">
                  <SelectValue placeholder="Selecione quem está saindo" />
                </SelectTrigger>
                <SelectContent className="bg-[#14151A] border-border rounded-none">
                  {dentro.map((r) => (
                    <SelectItem key={r.pessoa_id} value={r.pessoa_id}>{r.nome} {r.empresa ? `· ${r.empresa}` : ""}{r.cracha_temporario ? ` · crachá ${r.cracha_temporario}` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {regSaidaSel && (
              <div className="border border-border bg-[#0A0A0C] p-3 space-y-1" data-testid="saida-info-card">
                <div className="text-sm font-bold text-white">{regSaidaSel.nome}</div>
                <div className="text-[11px] text-muted-foreground">
                  {regSaidaSel.empresa || "sem empresa"} · Entrada: {fmtDT(regSaidaSel.hora_entrada)}
                </div>
                {(regSaidaSel.placa_cavalo || regSaidaSel.placa_carreta) && (
                  <div className="text-[11px] font-mono text-muted-foreground">Veículo: {regSaidaSel.placa_cavalo || "—"} / {regSaidaSel.placa_carreta || "—"}</div>
                )}
                {regSaidaSel.cracha_temporario && (
                  <div className="text-[11px] font-mono text-[#FF9F0A]" data-testid="saida-cracha-info">Crachá Provisório: <b>{regSaidaSel.cracha_temporario}</b></div>
                )}
              </div>
            )}
            {saidaComCarreta && (
              <div className="border border-[#FFD60A]/50 bg-[#FFD60A]/5 p-3 space-y-2" data-testid="saida-etapas-carreta">
                <div className="text-[11px] font-bold uppercase tracking-wider text-[#FFD60A] flex items-center gap-2">
                  <TruckIcon className="w-4 h-4" /> Saindo com a carreta — o que ela fez nesta visita?
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {saidaFins.map((k) => {
                    const cor = FIN_COR[k];
                    const on = !!saidaEtapas[k];
                    const label = k.startsWith("DESCARREGAR") ? FINALIDADES[k].replace("DESCARREGAR", "DESCARREGOU")
                      : k.startsWith("CARREGAR") ? FINALIDADES[k].replace("CARREGAR", "CARREGOU") : "FICOU EM STANDBY";
                    return (
                      <button key={k} type="button" onClick={() => setSaidaEtapas({ ...saidaEtapas, [k]: !on })} data-testid={`saida-etapa-${k}`}
                              className={`flex items-center gap-2 border-2 px-3 h-10 text-[10px] font-bold uppercase tracking-wider transition-colors ${on ? "" : "bg-transparent hover:bg-white/5"}`}
                              style={{ borderColor: cor, background: on ? cor : undefined, color: on ? "#000" : cor }}>
                        <span className={`w-4 h-4 shrink-0 border-2 flex items-center justify-center ${on ? "border-black bg-black/20" : ""}`} style={{ borderColor: on ? "#000" : cor }}>
                          {on && <CheckCircle2Icon className="w-3 h-3" strokeWidth={3} />}
                        </span>
                        {label}
                      </button>
                    );
                  })}
                </div>
                {saidaEtapas.DESCARREGAR_BACKLOAD && regSaidaSel.frota && (
                  <div className="space-y-1.5" data-testid="saida-pos-backload">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Frota {regSaidaSel.frota} descarregou o backload — como ela sai?</div>
                    <div className="grid grid-cols-2 gap-2">
                      <button type="button" data-testid="saida-sai-vazia"
                              onClick={() => { setSaidaEtapas({ ...saidaEtapas, CARREGAR_LOAD: false }); setSaidaDestino(""); }}
                              className={`border-2 px-3 h-auto min-h-10 py-1.5 text-[10px] font-bold uppercase tracking-wider leading-tight ${!saidaEtapas.CARREGAR_LOAD ? "bg-[#C7C7CC] text-black border-[#C7C7CC]" : "border-[#C7C7CC]/50 text-[#C7C7CC] hover:bg-white/5"}`}>
                        Sai VAZIA → fila Carreta vazia / Standby
                      </button>
                      <button type="button" data-testid="saida-sai-carregada"
                              onClick={() => { setSaidaEtapas({ ...saidaEtapas, CARREGAR_LOAD: true }); setSaidaDestino((d) => d || regSaidaSel.operacao?.destino || "PORTO"); }}
                              className={`border-2 px-3 h-auto min-h-10 py-1.5 text-[10px] font-bold uppercase tracking-wider leading-tight ${saidaEtapas.CARREGAR_LOAD ? "bg-[#34C759] text-black border-[#34C759]" : "border-[#34C759]/50 text-[#34C759] hover:bg-[#34C759]/10"}`}>
                        Sai CARREGADA → destino PORTO
                      </button>
                    </div>
                  </div>
                )}
                {saidaEtapas.CARREGAR_LOAD && regSaidaSel.frota && (
                  <div data-testid="saida-destino-bloco">
                    <Label className="text-[10px] uppercase tracking-wider text-[#34C759]">Destino do Load</Label>
                    <div className="flex gap-2 mt-1">
                      <Input data-testid="saida-destino" value={saidaDestino} onChange={(e) => setSaidaDestino(e.target.value.toUpperCase())} className={`${inputCls} font-mono flex-1`} placeholder="PORTO" />
                      {["PORTO", "CLIENTE", "OUTRA BASE"].map((d) => (
                        <button key={d} type="button" onClick={() => setSaidaDestino(d)} data-testid={`saida-destino-${d.replace(" ", "_")}`}
                                className={`border px-2 h-10 text-[10px] font-bold uppercase ${saidaDestino === d ? "bg-[#34C759] text-black border-[#34C759]" : "border-[#34C759]/50 text-[#34C759] hover:bg-[#34C759]/10"}`}>{d}</button>
                      ))}
                    </div>
                  </div>
                )}
                {saidaEtapas.DESCARREGAR_BACKLOAD && regSaidaSel.frota && !saidaEtapas.CARREGAR_LOAD && (
                  <div className="text-[10px] text-[#C7C7CC] border border-[#C7C7CC]/40 bg-white/5 px-2 py-1.5" data-testid="saida-aviso-standby">
                    FROTA {regSaidaSel.frota} descarregou o backload e sai vazia → após a saída vai automaticamente para a fila <b>CARRETA VAZIA / STANDBY</b>.
                  </div>
                )}
              </div>
            )}
            {regSaidaSel?.cracha_temporario && (
              <button
                type="button"
                onClick={() => setSaidaForm({ ...saidaForm, cracha_devolvido: !saidaForm.cracha_devolvido })}
                data-testid="saida-cracha-devolvido"
                className={`w-full flex items-center gap-4 border-2 p-4 text-left transition-colors cursor-pointer ${
                  saidaForm.cracha_devolvido
                    ? "border-[#34C759] bg-[#34C759]/10"
                    : "border-[#FF9F0A]/60 bg-[#FF9F0A]/5 hover:bg-[#FF9F0A]/15"
                }`}
              >
                <div className={`w-8 h-8 shrink-0 border-2 flex items-center justify-center ${
                  saidaForm.cracha_devolvido ? "border-[#34C759] bg-[#34C759]" : "border-[#FF9F0A]"
                }`}>
                  {saidaForm.cracha_devolvido && <CheckCircle2Icon className="w-6 h-6 text-black" strokeWidth={3} />}
                </div>
                <div>
                  <div className={`text-base font-black uppercase tracking-wide ${saidaForm.cracha_devolvido ? "text-[#34C759]" : "text-[#FF9F0A]"}`}>
                    {saidaForm.cracha_devolvido ? "Crachá Devolvido ✓" : "Crachá Devolvido na Portaria?"}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {saidaForm.cracha_devolvido
                      ? `Crachá ${regSaidaSel.cracha_temporario} será liberado para reuso por outra pessoa.`
                      : `Se NÃO devolveu, deixe desmarcado: a pessoa fica com o crachá ${regSaidaSel.cracha_temporario} e pode entrar/sair bipando-o no leitor.`}
                  </div>
                </div>
              </button>
            )}
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Motivo da Saída</Label>
              <Select value={saidaForm.motivo} onValueChange={(v) => setSaidaForm({ ...saidaForm, motivo: v })}>
                <SelectTrigger data-testid="saida-motivo" className="mt-1 rounded-none bg-[#0A0A0C] h-10 border-border"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#14151A] border-border rounded-none">
                  {MOTIVOS_SAIDA.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="border border-border bg-[#0A0A0C] p-3">
              <div className="text-[10px] uppercase tracking-widest text-[#00E5FF] mb-2 flex items-center gap-2">
                <TruckIcon className="w-3 h-3" /> Conferência de Ativos/Materiais na saída
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Qtd TQ</Label>
                  <Input data-testid="saida-qtd-tq" type="number" min="0" value={saidaForm.qtd_tq} onChange={(e) => setSaidaForm({ ...saidaForm, qtd_tq: e.target.value })} className={`${inputCls} font-mono`} />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Qtd RF</Label>
                  <Input data-testid="saida-qtd-rf" type="number" min="0" value={saidaForm.qtd_rf} onChange={(e) => setSaidaForm({ ...saidaForm, qtd_rf: e.target.value })} className={`${inputCls} font-mono`} />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Qtd Cesta</Label>
                  <Input data-testid="saida-qtd-cesta" type="number" min="0" value={saidaForm.qtd_cesta} onChange={(e) => setSaidaForm({ ...saidaForm, qtd_cesta: e.target.value })} className={`${inputCls} font-mono`} />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={registrarSaida} data-testid="saida-confirmar" className="rounded-none bg-[#FF9F0A] text-black hover:bg-[#FF9F0A]/85 font-bold uppercase tracking-wider">
              <LogOutIcon className="w-4 h-4 mr-2" /> Confirmar Saída
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
